/* eslint-disable no-plusplus */
import React, { useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { MoreOptions } from "../../../../components/MoreOptionsComponent/MoreOptions";
import TitlePage from "../../../../components/TitlePage";
import { PRIVATE_ROUTES } from "../../../../constants";
import useActionProduct from "../../../../hooks/useActionProducts";
import { Customer } from "../../../../models/customer";
import { CartonQuantities, OriginWarehouseProducts } from "../../../../models/product";
import { PurchaseOrder } from "../../../../models/purchase.order";
import { Searches } from "../../../../models/shipeezi.table";
import { getCustomerFullname } from "../../../../util/names";
import { ProductDetailsFields } from "./Maintenance/util";
import useStyles from "./styles";

const Products = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const classes = useStyles();

  const { getAllProducts, countProducts } = useActionProduct();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [searches, setSearches] = useState<Searches>([]);

  const { data: products, isFetching: loadingProducts } = getAllProducts(
    page,
    pageSize,
    searches,
    undefined,
    undefined,
    { endpointVersion: "v2" }
  );

  const { data: total, isFetching: loadingTotalItems } = countProducts(
    page,
    pageSize,
    searches,
    undefined,
    undefined,
    { endpointVersion: "v2" }
  );
  const totalItems = total ?? (products?.length || 0);

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleSearch = (searches: Searches) => {
    setSearches(searches);
    setPage(0);
  };

  const viewProduct = (id: number | string) => {
    history.push({
      pathname: `${PRIVATE_ROUTES.PRODUCTS_MNT}/${id}`,
      state: { viewOnly: true, productId: id },
    });
  };

  const editProduct = (id: string | number) => {
    history.push({
      pathname: PRIVATE_ROUTES.PRODUCTS_MNT,
      state: { productId: id },
    });
  };

  const getUniqueProjectNames = (orders: PurchaseOrder[]) => {
    if (!products || !products?.length || !orders?.length) return [];

    const projectNames = orders?.map((order) => order?.project?.name || "");

    return Array.from(new Set(projectNames)).sort((a, b) => a.localeCompare(b));
  };

  const getUniqueSupplierNames = (orders: PurchaseOrder[]) => {
    if (!orders?.length) return [];

    const supplierNames = orders?.map(({ supplierName }) => supplierName || "");

    return Array.from(new Set(supplierNames)).sort((a, b) => a.localeCompare(b));
  };

  const getOriginNames = (origins: OriginWarehouseProducts[]) => {
    if (!products || !products?.length || !origins?.length) return [];

    return origins.reduce((prev, curr) => {
      if (curr?.name) {
        prev.push(curr.name);
      }

      return prev;
    }, [] as string[]);
  };

  const columns: Column[] = [
    { name: ProductDetailsFields.SKU, title: `${translate("sku")} #` },
    { name: ProductDetailsFields.DESCRIPTION, title: translate("productName") },
    {
      name: ProductDetailsFields.PURCHASE_ORDERS,
      title: translate("suppliers"),
      formatComponent: (orders: PurchaseOrder[]) => {
        return (
          <MoreOptions
            options={getUniqueSupplierNames(orders)}
            title={translate("suppliers")}
            manyOptionsToShow={1}
          />
        );
      },
      hideSearch: true,
    },
    {
      name: ProductDetailsFields.CUSTOMER,
      title: translate("consignee"),
      formatText: (customer: Customer) => getCustomerFullname(customer),
      searchIdentifier: "customer.fullName",
    },
    {
      name: ProductDetailsFields.PURCHASE_ORDERS,
      title: translate("projects"),
      formatComponent: (orders: PurchaseOrder[]) => {
        return (
          <MoreOptions
            options={getUniqueProjectNames(orders)}
            title={translate("projects")}
            manyOptionsToShow={1}
          />
        );
      },
      hideSearch: true,
    },
    {
      name: ProductDetailsFields.STOCK,
      title: translate("currentStock"),
      hideSearch: true,
    },
    {
      name: ProductDetailsFields.CARTON_QUANTITIES,
      title: translate("unitsInTransit"),
      hideSearch: true,
      formatText: (quantities: CartonQuantities) => `${quantities?.inTransit || 0}`,
    },
    {
      name: ProductDetailsFields.ORIGIN_SHIPMENTS,
      title: translate("originShipments"),
      formatComponent: (origins: OriginWarehouseProducts[]) => {
        return (
          <MoreOptions
            options={getOriginNames(origins)}
            title={translate("originShipments")}
            manyOptionsToShow={1}
          />
        );
      },
      hideSearch: true,
    },
    {
      name: ProductDetailsFields.CARTON_QUANTITIES,
      title: translate("cartonsToDespatch"),
      hideSearch: true,
      formatText: (quantities: CartonQuantities) => `${quantities?.forDespatch || 0}`,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch"],
      right: ["handleNewRecord"],
    },
    handleSearch: {
      inputPlaceholder: translate("typePressEnter"),
      inputSearchMode: "enter",
      onSearch: handleSearch,
    },
    handleNewRecord: {
      text: translate("createProduct"),
      action: () =>
        history.push({
          pathname: PRIVATE_ROUTES.PRODUCTS_MNT,
        }),
      tooltip: translate("createProduct"),
    },
  };

  return (
    <>
      <TitlePage title={translate("products")} />
      <div className={classes.productsGridWrapper}>
        <ShipeeziNewGrid
          namePage={translate("products")}
          data={products || []}
          emptyData={translate("noInformationToBeShown")}
          totalItems={totalItems}
          columns={columns}
          loadingData={loadingProducts}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          rowActions={{
            handleEdit: (rowId) => editProduct(rowId),
            handleDetails: (rowId) => viewProduct(rowId),
          }}
          positionActionsColumn="right"
          showFooterGrid={totalItems !== 0 || loadingProducts || loadingTotalItems}
        />
      </div>
    </>
  );
};

export default Products;
